import { useCallback, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { FRONT_WARNING, IMPORT_SERVICES_PATH, WARNING } from '@app/constants';
import { FileContext, NotificationContext } from '@app/providers';

import type { NServicesSorting } from '@pages/Clients/ServicesSorting/ServicesSorting.types';

import type { ChangeEvent } from 'react';

const EXCEL_EXTENSIONS = ['xls', 'xlsx'];

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Не удалось прочитать файл'));
        return;
      }

      resolve(reader.result.replace('data:', '').replace(/^.+,/, ''));
    };
    reader.onerror = () => {
      reject(reader.error || new Error('Не удалось прочитать файл'));
    };
    reader.readAsDataURL(file);
  });

export const ServicesImport: NServicesSorting.ServicesImport = ({
  inputId,
}) => {
  const navigate = useNavigate();
  const { setFile, setExtension } = useContext(FileContext);
  const { showNotification } = useContext(NotificationContext);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const openFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.target;
      const file = input.files?.[0];
      const filename = file?.name || '';
      const extension = filename.split('.').pop()?.toLowerCase() || '';

      if (!file) {
        showNotification('Файл не найден', FRONT_WARNING);
        input.value = '';
        return;
      }

      if (!EXCEL_EXTENSIONS.includes(extension)) {
        showNotification('Неизвестное расширение файла', FRONT_WARNING);
        input.value = '';
        return;
      }

      try {
        const data = await readFileAsBase64(file);

        setFile(data);
        setExtension(extension);

        // Импорт услуг выполняется в фоновом режиме: уходим на отдельную
        // страницу с live-прогрессом через SignalR (по аналогии с клиентами
        // и записями).
        navigate(IMPORT_SERVICES_PATH, {
          state: { file: data, extension },
        });
      } catch (error) {
        showNotification(error, WARNING);
      } finally {
        input.value = '';
      }
    },
    [navigate, setExtension, setFile, showNotification]
  );

  return (
    <div onClick={openFilePicker} className="unselectable">
      <span data-testid="import-from-file">Импорт из файла</span>
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        id={inputId}
        accept=".xlsx, .xls"
        onChange={handleFileChange}
      />
    </div>
  );
};
